<template>
  <div class="flex justify-between border-b border-main p-4 last:border-b-0" data-testid="documents-list-item">
    <div>
      <RouterLink
        class="text-headline-s text-main visited:text-main"
        :to="{ name: 'documentDetails', params: { signableId: signableId } }"
        data-testid="documents-list-item-link"
      >
        {{ label }}
      </RouterLink>
      <DocumentItemData
        v-if="props.documentable.__typename === 'Booth'"
        class="mt-4 opacity-75"
        :booth-number="props.documentable.boothNumber"
        :type-display-name="boothDisplayName"
      />
      <DocumentItemData
        v-else-if="props.documentable.__typename === 'Event'"
        class="mt-4 opacity-75"
        :type-display-name="props.documentable.eventType"
      />
      <DocumentItemData
        v-else-if="props.documentable.__typename === 'PartnerCompany'"
        class="mt-4 opacity-75"
        :type-display-name="`Partner Company #${toPlainId(props.documentable.id)}`"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { DocumentablesQuery } from '@/gql/myomr'
import { toPlainId } from '@/helpers/globalIdHelper'
import { getLocalizedAttribute } from '@/helpers/localizedAttribute'
import { urlifyGlobalId } from '@/helpers/urlifyGlobalId'

import DocumentItemData from './DocumentItemData.vue'

const i18n = useI18n()

const props = defineProps<{
  documentable: DocumentablesQuery['documentables']['nodes'][number]
}>()

const signableId = computed(() => urlifyGlobalId(props.documentable.id))

const boothDisplayName = computed(() => {
  if (props.documentable.__typename !== 'Booth') return null
  if (props.documentable.typeDisplayName === 'Branded Room') return 'Exclusive Room'
  return props.documentable.typeDisplayName
})

const label = computed(() => {
  const d = props.documentable
  if (d.__typename === 'Booth') {
    const partnerCompanyLabel = d.partnerCompany?.displayName || d.partnerCompany?.name
    if (partnerCompanyLabel) return partnerCompanyLabel
    const boothTitle = getLocalizedAttribute({ de: d.titleDe, en: d.titleEn, fallback: '', locale: i18n.locale.value })
    if (boothTitle) return boothTitle
    if (d.boothNumber && d.boothNumber.length > 0) return d.boothNumber
    return signableId.value
  }
  if (d.__typename === 'Event') {
    return getLocalizedAttribute({ de: d.nameDe, en: d.nameEn, fallback: signableId.value, locale: i18n.locale.value })
  }
  if (d.__typename === 'PartnerCompany') {
    return d.displayName || d.name || signableId.value
  }
  return signableId.value
})
</script>
